/**
 * Списки в надписи — как в Miro: «- » в начале строки становится «• »,
 * «1. » — нумерованным пунктом, Enter продолжает список, Tab вкладывает
 * пункт, Enter на пустом пункте выводит уровнем выше, а затем из списка.
 *
 * Маркер хранится прямо в тексте строки. Так надпись остаётся обычным
 * текстом: старые версии доски показывают её как есть, размер рамки
 * считается тем же измерением, а при копировании маркеры едут вместе с
 * текстом. Вся «умная» часть — здесь, в чистых функциях над строкой и
 * местом курсора; поле ввода только применяет их результат.
 */

/** Один уровень вложенности. Широкий пробел — ширина не зависит от шрифта. */
export const LIST_INDENT = "  ";

/** Самый глубокий уровень — дальше Tab ничего не делает. */
export const LIST_MAX_LEVEL = 4;

/** Значок маркера по уровню: •, ◦, ▪ и дальше по кругу. */
const BULLETS = ["•", "◦", "▪"] as const;

export type ListKind = "bullet" | "number";

export type ListLine = {
  level: number;
  kind: ListKind | null;
  /** Номер пункта, если список нумерованный. */
  number?: number;
  /** Длина отступа + маркера + пробела: где начинается сам текст пункта. */
  prefixLength: number;
  /** Текст пункта без отступа и маркера. */
  content: string;
};

export function bulletFor(level: number): string {
  return BULLETS[((level % BULLETS.length) + BULLETS.length) % BULLETS.length]!;
}

function indentFor(level: number): string {
  return LIST_INDENT.repeat(Math.max(0, level));
}

/** Разобрать строку: отступ, маркер, текст. */
export function parseListLine(line: string): ListLine {
  let level = 0;
  let pos = 0;
  while (line.startsWith(LIST_INDENT, pos)) {
    level += 1;
    pos += LIST_INDENT.length;
  }
  const rest = line.slice(pos);
  const bullet = /^([•◦▪])[  ]/.exec(rest);
  if (bullet) {
    const prefixLength = pos + bullet[0].length;
    return { level, kind: "bullet", prefixLength, content: line.slice(prefixLength) };
  }
  const num = /^(\d{1,3})[.)][  ]/.exec(rest);
  if (num) {
    const prefixLength = pos + num[0].length;
    return {
      level,
      kind: "number",
      number: Number(num[1]),
      prefixLength,
      content: line.slice(prefixLength),
    };
  }
  return { level, kind: null, prefixLength: pos, content: rest };
}

export function listPrefix(level: number, kind: ListKind, number = 1): string {
  return indentFor(level) + (kind === "bullet" ? `${bulletFor(level)} ` : `${number}. `);
}

/** Правка текста: заменить [start, end) на text, курсор поставить в caret. */
export type TextEdit = { start: number; end: number; text: string; caret: number };

/** Начало и конец строки, в которой стоит позиция. */
export function lineBounds(text: string, offset: number): { start: number; end: number } {
  const start = text.lastIndexOf("\n", Math.max(0, offset - 1)) + 1;
  const nl = text.indexOf("\n", offset);
  return { start: offset === 0 ? 0 : start, end: nl < 0 ? text.length : nl };
}

/**
 * Пробел после «-» или «*» в начале строки превращает её в пункт списка.
 * `caret` — позиция сразу после набранного пробела.
 */
export function autoformatAfterSpace(text: string, caret: number): TextEdit | null {
  const { start } = lineBounds(text, caret);
  const before = text.slice(start, caret);
  const m = /^((?:  )*)[-*][  ]$/.exec(before);
  if (!m) return null;
  const level = m[1]!.length / LIST_INDENT.length;
  const prefix = listPrefix(level, "bullet");
  return { start, end: caret, text: prefix, caret: start + prefix.length };
}

/**
 * «- » или «* » в начале строки под курсором — сделать пунктом списка.
 *
 * Проверяем строку целиком, а не «последний набранный символ»: телефонная
 * клавиатура, автозамена и вставка кладут текст кусками, и отдельного
 * события «нажат пробел» может не быть вовсе.
 */
export function autoformatLine(text: string, caret: number): TextEdit | null {
  const { start } = lineBounds(text, caret);
  const m = /^((?:\u2003\u2003)*)[-*][ \u00A0]/.exec(text.slice(start));
  if (!m) return null;
  // Курсор ещё внутри «- » — человек только набирает, не мешаем.
  if (caret < start + m[0].length) return null;
  const level = m[1]!.length / LIST_INDENT.length;
  const prefix = listPrefix(level, "bullet");
  return {
    start,
    end: start + m[0].length,
    text: prefix,
    caret: caret + (prefix.length - m[0].length),
  };
}

/**
 * Enter внутри списка. null — строка не пункт списка, пусть Enter работает
 * как обычно.
 */
export function enterInList(text: string, caret: number): TextEdit | null {
  const { start, end } = lineBounds(text, caret);
  const line = parseListLine(text.slice(start, end));
  if (!line.kind) return null;
  // Курсор внутри маркера — это не «продолжить список», а обычный перенос.
  if (caret < start + line.prefixLength) return null;

  if (!line.content.trim()) {
    // Пустой пункт: сначала уровнем выше, потом совсем из списка.
    if (line.level > 0) {
      const prefix = listPrefix(line.level - 1, line.kind, 1);
      return { start, end, text: prefix, caret: start + prefix.length };
    }
    return { start, end, text: "", caret: start };
  }

  const next = listPrefix(line.level, line.kind, (line.number ?? 0) + 1);
  const insert = `\n${next}`;
  return { start: caret, end: caret, text: insert, caret: caret + insert.length };
}

/** Tab / Shift+Tab: вложить пункт или вынести уровнем выше. */
export function indentListLine(text: string, caret: number, delta: 1 | -1): TextEdit | null {
  const { start, end } = lineBounds(text, caret);
  const line = parseListLine(text.slice(start, end));
  if (!line.kind) return null;
  const level = line.level + delta;
  if (level < 0 || level > LIST_MAX_LEVEL) return null;
  // Вложенный нумерованный пункт начинает счёт заново, вынесенный — тоже.
  const prefix = listPrefix(level, line.kind, 1);
  const shift = prefix.length - line.prefixLength;
  return {
    start,
    end: start + line.prefixLength,
    text: prefix,
    caret: Math.max(start + prefix.length, caret + shift),
  };
}

/**
 * Backspace сразу после маркера убирает маркер, а не стирает его по букве:
 * пункт становится обычной строкой.
 */
export function backspaceAtMarker(text: string, caret: number): TextEdit | null {
  const { start, end } = lineBounds(text, caret);
  const line = parseListLine(text.slice(start, end));
  if (!line.kind || caret !== start + line.prefixLength) return null;
  return { start, end: start + line.prefixLength, text: "", caret: start };
}

/**
 * Кнопка списка на панели: строки, которых касается выделение, становятся
 * пунктами нужного вида (нумерация подряд) — или перестают быть ими, если
 * уже все такие. Возвращает правку на весь затронутый кусок.
 */
export function toggleListLines(
  text: string,
  selStart: number,
  selEnd: number,
  kind: ListKind
): TextEdit {
  const from = lineBounds(text, Math.min(selStart, selEnd)).start;
  const to = lineBounds(text, Math.max(selStart, selEnd)).end;
  const lines = text.slice(from, to).split("\n");
  const parsed = lines.map(parseListLine);
  const allAlready = parsed.every((l) => l.kind === kind);
  let n = 0;
  const out = parsed.map((l) => {
    if (allAlready) return indentFor(l.level) + l.content;
    n += 1;
    return listPrefix(l.level, kind, n) + l.content;
  });
  const replaced = out.join("\n");
  return { start: from, end: to, text: replaced, caret: from + replaced.length };
}

/** Применить правку к строке — для тестов и для надписи без открытого поля. */
export function applyTextEdit(text: string, edit: TextEdit): string {
  return text.slice(0, edit.start) + edit.text + text.slice(edit.end);
}
